import React from "react";
import { buildSplitConsentCopy } from "../../config/splitConsentOrgs";
import {
  replaceDynamicTags,
  withPolicyLinks,
} from "../../utils/dynamicTagsUtils";

interface SplitConsentCheckboxesProps {
  orgName: string;
  /** Calls-and-email opt-in. This is the value that feeds `marketingConsent`. */
  callsAndEmailConsent: boolean;
  onCallsAndEmailConsentChange: (checked: boolean) => void;
  /** Text-message choice is submitted as its own receipt. */
  textConsent: boolean;
  onTextConsentChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Wrapper typography, so each surface keeps its own text sizing. */
  className?: string;
  /** Prefix for the input ids, keeping them unique per surface. */
  idPrefix: string;
}

const CHECKBOX_CLASSES =
  "mt-[1px] shrink-0 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500";

// Naming the policy paths explicitly is what makes replaceDynamicTags emit
// target="_blank" rel="noopener noreferrer", so reading a policy opens a new tab
// and leaves the half-filled sign-up form untouched behind it.
const POLICY_LINKS = {
  termsOfUseLink: "/terms",
  privacyLink: "/privacy",
};

export const SplitConsentCheckboxes: React.FC<SplitConsentCheckboxesProps> = ({
  orgName,
  callsAndEmailConsent,
  onCallsAndEmailConsentChange,
  textConsent,
  onTextConsentChange,
  disabled = false,
  className = "",
  idPrefix,
}) => {
  const copy = buildSplitConsentCopy(orgName);

  // SMS opt-in renders first, matching the existing split-copy order.
  const rows = [
    {
      id: `${idPrefix}-consent-text`,
      channel: "sms",
      checked: textConsent,
      onChange: onTextConsentChange,
      html: replaceDynamicTags(copy.text, POLICY_LINKS),
    },
    {
      id: `${idPrefix}-consent-calls-email`,
      channel: "calls-email",
      checked: callsAndEmailConsent,
      onChange: onCallsAndEmailConsentChange,
      html: replaceDynamicTags(
        withPolicyLinks(copy.callsAndEmail),
        POLICY_LINKS
      ),
    },
  ];

  return (
    <div className={`space-y-2 ${className}`}>
      {rows.map((row) => {
        const textId = `${row.id}-text`;
        return (
          <div key={row.id} className="flex items-start gap-1.5">
            <input
              id={row.id}
              type="checkbox"
              checked={row.checked}
              onChange={(e) => row.onChange(e.target.checked)}
              disabled={disabled}
              aria-labelledby={textId}
              className={CHECKBOX_CLASSES}
            />
            {/* The consent wording carries the Terms and Privacy links, so it is
                a span rather than a label: a link inside a label toggles the
                checkbox as well as following the href, which would tick the box
                for someone who only wanted to read the policy. aria-labelledby
                keeps the wording as the checkbox's accessible name. */}
            <span
              id={textId}
              data-consent-copy={row.channel}
              dangerouslySetInnerHTML={{ __html: row.html }}
            />
          </div>
        );
      })}
    </div>
  );
};

export default SplitConsentCheckboxes;
